import { get } from 'lodash';
import capitalize from 'lodash/capitalize';
import lowerCase from 'lodash/lowerCase';
import { useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { DividendOperationInfoModal } from '@/modules/issuer-dashboard/components/distribution-dashboard/DividendOperationInfoModal.tsx';
import { IssuerConfirmPaymentModal } from '@/modules/issuer-dashboard/components/modals/IssuerConfirmPaymentModal.tsx';
import { InfoItem } from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import { useIssuerActivitiesListQuery } from '@/modules/issuer-dashboard/queries/activities.tsx';
import {
  usePaymentByIdQuery,
  usePaymentSetRejectedMutation,
} from '@/modules/issuer-dashboard/queries/payments.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Badge,
  BaseModalProps,
  Button,
  DeclineModal,
  ModalContentWrapper,
  Spinner,
  Tooltip,
} from 'ui';
import { InfoCircleWhiteIcon } from 'ui/icons';
import { fixNameEnum, goodFormatUSDCurrencyLowPrecision } from 'ui/utils';
import {
  EntityStatus,
  PaymentExtend,
  PaymentSetRejectedStatusRequest,
  TransactionStatus,
} from 'api';

interface IssuerActivitiesOperationInfoModalProps extends BaseModalProps {
  paymentId: number;
  registerId: string;
  registerCountry: string;
  isDeleted?: boolean;
}

export const IssuerActivitiesOperationInfoModal = ({
  paymentId,
  registerId,
  registerCountry,
  open,
  onClose,
  isDeleted = false,
}: IssuerActivitiesOperationInfoModalProps) => {
  const [openConfirmPayment, setOpenConfirmPayment] = useState(false);
  const [openRejectPayment, setOpenRejectPayment] = useState(false);
  const [openDividend, setOpenDividend] = useState(false);
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { data, isLoading: isLoadingItem } = usePaymentByIdQuery({
    id: paymentId,
    is_deleted: isDeleted,
  });
  const itemPayment = get(data, 'data') as PaymentExtend;
  const { data: activitiesItems, isLoading: isLoadingList } = useIssuerActivitiesListQuery({
    dividend_id: itemPayment?.dividend_event_id,
    page_size: 50,
    page: 1,
    is_dividend_activities: !!itemPayment?.dividend_event_id,
    register_class_id:
      itemPayment?.transaction?.register_class_id || itemPayment?.dividend_event?.register_class_id,
    is_deleted: isDeleted,
  });
  const isLoading = isLoadingItem || isLoadingList;

  const item = useMemo(() => {
    const activityList = (activitiesItems?.pages || []).flatMap(({ data }) => data);
    return activityList?.find(item => item.payment_id === paymentId);
  }, [activitiesItems?.pages, paymentId]);
  const { mutateAsync: rejectPayment } = usePaymentSetRejectedMutation();

  return (
    <ModalContentWrapper
      variant="right"
      title={
        <div className="flex gap-4 items-center">
          <span>Payment info</span>
          {isDeleted && (
            <Badge containerClass="bg-red-400 text-white text-xs font-[500] font-[Inter]">
              Archived
            </Badge>
          )}
        </div>
      }
      open={open}
      onClose={onClose}
    >
      {isLoading ? (
        <div className="flex flex-col justify-start pt-10 items-center gap-4 h-full">
          <Spinner size="lg" />
        </div>
      ) : (
        item && (
          <>
            <div className="flex flex-col justify-between gap-4 h-full">
              <div className="space-y-3">
                <InfoItem title="Payment ID">
                  <div>#{paymentId}</div>
                </InfoItem>
                <InfoItem title="Transaction ID">
                  <div>#{item.id}</div>
                </InfoItem>
                {itemPayment?.dividend_event_id && (
                  <InfoItem
                    title={`${getRegisterTypeStrings('dividend', { capitalize: true })} event ID`}
                  >
                    <div
                      className="text-blue-700 cursor-pointer hover:underline"
                      onClick={() => setOpenDividend(true)}
                    >
                      #{itemPayment?.dividend_event_id}
                    </div>
                  </InfoItem>
                )}
                <InfoItem title="Payment Status">
                  <ActivityStatusBadge status={itemPayment.status ?? '-'} />
                </InfoItem>
                <InfoItem title="Transaction Status">
                  <ActivityStatusBadge status={item.status ?? '-'} />
                </InfoItem>
                <InfoItem title="Direction">{fixNameEnum(itemPayment.direction)}</InfoItem>
                <InfoItem title="Class">
                  <Link
                    state={{ from: pathname + '?' + params.toString() }}
                    to={{
                      pathname: issuerDashboardPaths.dynamic.classById(
                        (item?.register_class_id || '0').toString(),
                      ),
                      search: params.toString(),
                    }}
                    className="text-blue-700 hover:underline"
                  >
                    {item?.register_class_name || '-'}
                  </Link>
                </InfoItem>

                <InfoItem title="Type">
                  <div>{capitalize(lowerCase(item.type))}</div>
                </InfoItem>
                <InfoItem title="Holder">
                  <Link
                    state={{ from: pathname + '?' + params.toString() }}
                    to={{
                      pathname: issuerDashboardPaths.dynamic.holder(
                        (item.from_entity_id || '0').toString(),
                      ),
                      search: params.toString(),
                    }}
                    className="text-blue-700 hover:underline"
                  >
                    {item.from_entity_name}
                  </Link>
                </InfoItem>
                <InfoItem title="Payment Amount">
                  <div>{goodFormatUSDCurrencyLowPrecision(itemPayment.amount)}</div>
                </InfoItem>
                <InfoItem title="Transaction Amount">
                  <div>{goodFormatUSDCurrencyLowPrecision(item.amount)}</div>
                </InfoItem>
                <InfoItem title="Requested date">
                  <div>{dateToPrint(item.requested_at_date, DateVariant.Information)}</div>
                </InfoItem>
                <InfoItem title="Completion date">
                  {item.status === TransactionStatus.Completed
                    ? dateToPrint(item.completion_date, DateVariant.Information)
                    : '-'}
                </InfoItem>

                <InfoItem title="Memo">
                  <div>{item.memo || '-'}</div>
                </InfoItem>
              </div>
              {!isDeleted && (
                <div className="flex flex-col gap-4 w-full">
                  {item?.status === TransactionStatus.Pending &&
                    !!item.payment_id &&
                    item.source === 'payment' && (
                      <>
                        <Button
                          className="min-w-[90px]"
                          disabled={item.entity_status !== EntityStatus.EntityApproved}
                          onClick={() => {
                            setOpenConfirmPayment(true);
                          }}
                        >
                          <span>Confirm</span>
                          {item.entity_status !== EntityStatus.EntityApproved && (
                            <Tooltip
                              position="up"
                              positionHorizontal="right"
                              variant="gray"
                              delayTimeOpen={0}
                              delayTimeClose={0}
                              button={
                                <img
                                  alt="info"
                                  src={InfoCircleWhiteIcon}
                                  className="w-[18px] h-[18px] min-h-[18px] min-w-[18px]"
                                />
                              }
                            >
                              <span>
                                You can't confirm the transaction if Entity is not approved
                              </span>
                            </Tooltip>
                          )}
                        </Button>

                        <Button
                          variant="secondary"
                          className="min-w-[90px]"
                          onClick={() => {
                            setOpenRejectPayment(true);
                          }}
                        >
                          Decline
                        </Button>
                      </>
                    )}
                </div>
              )}
            </div>
            {!!item?.payment_id && !!item?.amount && (
              <IssuerConfirmPaymentModal
                open={openConfirmPayment}
                onClose={() => setOpenConfirmPayment(false)}
                payment_id={item?.payment_id}
                price_per_share={item?.price_per_share || 0}
                amount={item?.amount}
              />
            )}

            {!!item?.payment_id && (
              <DeclineModal
                onClose={() => setOpenRejectPayment(false)}
                open={openRejectPayment}
                onConfirm={async (payload: PaymentSetRejectedStatusRequest) => {
                  await rejectPayment({ ...payload, id: item?.payment_id || 0 });
                }}
              />
            )}

            {!!itemPayment?.dividend_event_id && openDividend && (
              <DividendOperationInfoModal
                open={openDividend}
                id={itemPayment?.dividend_event_id}
                onClose={() => setOpenDividend(false)}
                registerId={registerId}
                registerCountry={registerCountry}
                isDeleted={isDeleted}
              />
            )}
          </>
        )
      )}
    </ModalContentWrapper>
  );
};
